/**
 * CON-1487 — HTML to plain-text helper for Event Template previews.
 *
 * Strips tags, decodes a handful of common named + numeric HTML
 * entities, and preserves PARAGRAPH BREAKS so a list cell rendered
 * with `white-space: pre-wrap` still shows the author's line spacing.
 *
 * Block-level tag openers (`<br>`, `<p>`, `<div>`, `<li>`) are turned
 * into `\n` BEFORE tag stripping so email bodies (which store `<br>`
 * separators) render identically to SMS bodies (which store `\n`).
 * Inline whitespace is collapsed to single spaces within a line, but
 * newlines are preserved as-is.
 *
 * Sufficient for rendering a truncated preview of sanitized HTML
 * email bodies in a list cell — NOT a full HTML parser, and not a
 * security boundary. The EmailComposer already sanitizes content
 * before it hits the DB.
 *
 * @param {string} input  HTML or plain text.
 * @returns {string}      plain text with paragraph breaks preserved.
 */
export function stripHtml(input) {
  if (input === null || input === undefined) return '';
  const str = String(input);
  if (str.length === 0) return '';

  return str
    // Turn block-level tags into line breaks before stripping. We
    // match `<br>`, `<br/>`, `<br />`, `<p>`, `</p>`, `<div>`,
    // `</div>`, `<li>`, `</li>` case-insensitively. `</p>` closes get
    // a double newline to preserve paragraph spacing across <p>..</p>
    // email templates.
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*p\s*>/gi, '\n\n')
    .replace(/<\s*p(\s[^>]*)?\s*>/gi, '')
    .replace(/<\s*\/?\s*(div|li)(\s[^>]*)?\s*>/gi, '\n')
    // Now strip everything else.
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_m, n) => String.fromCharCode(parseInt(n, 10)))
    // Collapse horizontal whitespace only; keep newlines intact.
    .replace(/[ \t]+/g, ' ')
    // Trim per-line edges so stripped tags don't leave ragged spaces.
    .replace(/ *\n/g, '\n')
    .replace(/\n */g, '\n')
    // Collapse runs of 3+ newlines to exactly 2 (one blank line
    // between paragraphs — matches author intent without runaway
    // spacing in the list cell).
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Truncate a string to `maxLength`, appending ellipsis if truncated.
 */
export function truncate(input, maxLength = 60) {
  const str = String(input || '');
  if (str.length <= maxLength) return str;
  return `${str.slice(0, Math.max(0, maxLength - 1))}…`;
}
